import { fetchResults } from './fetchResults.js';
import { getQueryParam, updateQueryString, showOrHideClearButton } from '../shared/utils.js';
import {
    tabQueryParam,
    searchTypeQueryParam,
    typeSearchDelay,
    searchButtonHiddenClass,
    corporateDocsValue,
    searchInputQueryParam,
    showClearAllButton,
    hiddenClass,
    resourceTypeParam
} from '../../../config.js';
import { elements } from './elements.js';
import { initializeExpandableFilter } from '../../../includes/collapseFilterGroup.js';

const clearFormExclusions = [tabQueryParam, searchTypeQueryParam, searchInputQueryParam, 'sort'];

export const updateForm = (searchForm) => {
    const selectedTab = getQueryParam(tabQueryParam) || 'pages';
    fetchResults();
    updateQueryString(searchForm);
    updateQueryString({ [tabQueryParam]: selectedTab });
    showOrHideClearButton(elements.clearAllButton, searchForm, clearFormExclusions)
}

const bindSortDropdown = () => {
    const { sortDropdown, searchForm } = elements;
    if (!sortDropdown || !searchForm) return;

    sortDropdown.addEventListener('change', () => {
        updateForm(searchForm);
    });
};

const bindNonSortDropdowns = () => {
    const { dropdowns, searchForm } = elements;
    if (!dropdowns?.length || !searchForm) return;

    dropdowns.forEach(dropdown => {
        dropdown.addEventListener('change', () => {
            updateForm(searchForm);
        });
    });
};

const bindSearchInput = () => {
    const { searchInput, clearSearchButton, searchButton, searchForm } = elements;
    if (!searchInput || !clearSearchButton || !searchButton || !searchForm) return;

    let typingTimer;

    searchInput.addEventListener('input', () => {
        clearTimeout(typingTimer);
        typingTimer = setTimeout(() => {
            updateForm(searchForm);
        }, typeSearchDelay);

        if (searchInput.value) {
            clearSearchButton.classList.remove(searchButtonHiddenClass);
            searchButton.classList.add(searchButtonHiddenClass);
        } else {
            clearSearchButton.classList.add(searchButtonHiddenClass);
            searchButton.classList.remove(searchButtonHiddenClass);
        }
    });

    clearSearchButton.addEventListener('click', () => {
        searchInput.value = '';
        clearSearchButton.classList.add(searchButtonHiddenClass);
        searchButton.classList.remove(searchButtonHiddenClass);
        updateForm(searchForm);
    });
};

const bindClearAllButton = (resourceChoices) => {
    const {
        clearAllButton,
        searchForm,
        updatedAfterInput,
        updatedBeforeInput,
        searchTypeRadios,
        clearDateButtons
    } = elements;

    if (!clearAllButton || !searchForm || !updatedAfterInput || !updatedBeforeInput) return;

    searchForm.addEventListener('change', () => showOrHideClearButton(clearAllButton, searchForm, clearFormExclusions));
    clearAllButton.addEventListener('click', () => {
        updatedAfterInput.value = '';
        updatedBeforeInput.value = '';
        clearDateButtons.forEach(clearDateButton => clearDateButton.classList.add(hiddenClass));
        clearAllButton.classList.remove(showClearAllButton);

        if (searchTypeRadios.length) {
            // Reset the resource type dropdown if searchTypeValue is not corporate docs
            const searchTypeValue = getQueryParam(searchTypeQueryParam);
            if (searchTypeValue !== corporateDocsValue && resourceChoices) {
                resourceChoices.setChoiceByValue('');
            }
        } else {
            // Only reset all dropdowns if searchTypeRadios does not exist
            if (resourceChoices) {
                resourceChoices.setChoiceByValue('');
            }
        }

        fetchResults();

        const url = new URL(window.location);
        const keys = [...url.searchParams.keys()]
        keys.forEach((key) => {
            if (!clearFormExclusions.includes(key)) {
                url.searchParams.delete(key);
            }
        });

        history.pushState(null, '', url);
    });
    showOrHideClearButton(clearAllButton, searchForm, clearFormExclusions)
};

const bindFormSubmission = () => {
    const { searchForm } = elements;
    if (!searchForm) return;

    searchForm.addEventListener('submit', (event) => {
        event.preventDefault();
        event.stopImmediatePropagation();
        updateForm(searchForm);
    });
};

const lockResourceTypeDropdown = (resourceChoices, lock = true) => {
    if (!resourceChoices) return;

    if (lock) {
        resourceChoices.setChoiceByValue(corporateDocsValue);
        resourceChoices.disable();
        return;
    }

    resourceChoices.setChoiceByValue('');
    resourceChoices.enable();
}

const bindSearchTypeRadioButtons = (resourceChoices) => {
    const { searchForm, searchTypeRadios, resourceTypeDropdown } = elements;

    if (!searchTypeRadios.length || !resourceTypeDropdown || !searchForm) return;

    searchTypeRadios.forEach(radio => {
        radio.addEventListener('change', (event) => {
            const selectedValue = event.target.value;

            // Update query string
            const url = new URL(window.location);
            if (selectedValue === corporateDocsValue) {
                url.searchParams.set(resourceTypeParam, corporateDocsValue);
                lockResourceTypeDropdown(resourceChoices);
            } else {
                url.searchParams.delete(resourceTypeParam);
                lockResourceTypeDropdown(resourceChoices, false);
            }

            history.pushState(null, '', url);

            updateForm(searchForm);
            fetchResults();
        });
    });
};

export const bindFormElements = (allChoices) => {
    bindSortDropdown();
    bindNonSortDropdowns();
    bindSearchInput();
    bindClearAllButton(allChoices[resourceTypeParam]);
    bindFormSubmission();
    bindSearchTypeRadioButtons(allChoices[resourceTypeParam]);
    initializeExpandableFilter(elements.expandFilterButton, elements.expandableFilter);
};
